import { omit } from 'lodash';
import { useInputController } from '../hooks/useInputController.ts';
import { IsRadioOrCheckboxGroupElementSymbol } from '../item/symbols/isRadioOrCheckboxGroupElement.tsx';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';
import {
  CheckableTree,
  type ICheckableTreeProps,
} from './tree/CheckableTree.tsx';

export interface ICheckableTreeInputProps
  extends Omit<ICheckableTreeProps, 'value' | 'onChange'> {
  name: string;
  /**
   * Invocata dopo l'aggiornamento del field, con il nuovo insieme di chiavi
   * spuntate. Utile per applicare effetti collaterali (pattern `CheckboxInput`).
   */
  onAfterChange?: (value: string[]) => void;
}

/**
 * Wrapper di form dell'albero checkable: lega {@link CheckableTree} a
 * react-hook-form tramite {@link useInputController}. Il valore del campo
 * `name` è l'array di chiavi spuntate (`string[]`).
 *
 * Come gli altri input "di gruppo" (es. `CheckboxGroupInput`) non ha un
 * singolo elemento focalizzabile: la label è associata via `aria-labelledby`
 * su un contenitore `role="group"` (vedi
 * {@link IsRadioOrCheckboxGroupElementSymbol}).
 */
export function CheckableTreeInput(props: ICheckableTreeInputProps) {
  const { name, onAfterChange, ...treeProps } = props;

  const { enabled, visuallyDisabled } = useFormItemEnabled();
  const {
    field: { value, onChange, ref },
  } = useInputController<string[]>(name, { enabled });

  const accessibility = useFormItemAccessibility();

  return (
    <div
      role="group"
      ref={ref}
      aria-labelledby={accessibility.labelId}
      {...omit(accessibility.attributes, 'id')}
    >
      <CheckableTree
        {...treeProps}
        disabled={visuallyDisabled}
        value={value ?? []}
        onChange={keys => {
          onChange(keys);
          onAfterChange?.(keys);
        }}
      />
    </div>
  );
}

CheckableTreeInput[IsRadioOrCheckboxGroupElementSymbol] = true;
